// Rivals pieces shared by the Rivals tab, Today, Results and You vs a rival: one edition's card
// (everyone you're up against that day), your turn, a past day's row, a rival's row, and the one
// strip Today keeps for challenges.
import type { ReactNode } from 'react'
import type { ChallengePlayer, Rival } from '@wbwl/contract'
import { Link } from 'react-router'
import { dayDate, fmt, weekday } from '../lib/game.ts'
import { namesOf, outcome, resultAgainst, type Day } from '../lib/rivals.ts'
import { NameBadge } from './Badge.tsx'
import { Form, progressText, recordText } from './challenge-ui.tsx'
import { Icon } from './Icon.tsx'
import { Avatar } from './shell.tsx'

const DAY_MS = 86_400_000

/** Who sent the invites: each invite's creator. */
const inviters = (invites: Day['invites']) => invites.map((c) => c.players.find((p) => p.creator)).filter((p): p is ChallengePlayer => !!p)

/** Whole days until the day's challenges close to newcomers (the latest of them). */
export function daysLeft(d: Day, now = Date.now()): number {
  const last = Math.max(...d.challenges.map((c) => Date.parse(c.expiresAt)))
  return Math.max(0, Math.ceil((last - now) / DAY_MS))
}

export function Stack({ players, size = 30, max = 3 }: { players: readonly { id: string; displayName: string; avatar: string | null }[]; size?: number; max?: number }) {
  return <span className="stack">{players.slice(0, max).map((p) => <Avatar key={p.id} id={p.id} name={p.displayName} src={p.avatar} size={size} />)}</span>
}

/** What you do to open something: tap on a touch screen, click with a mouse. */
const press = typeof matchMedia === 'function' && matchMedia('(hover: none)').matches ? 'tap' : 'click'

/**
 * A player that day: rank or how far they've got, score (hidden while new), and your result against
 * them. A hidden score opens the boards (`to`), where it's revealed.
 */
function PlayerRow({ p, day, to }: { p: ChallengePlayer; day: Day; to?: string }) {
  const hidden = p.fresh && p.done && !p.mine
  const r = resultAgainst(day, p)
  const cells = (
    <>
      <Avatar id={p.id} name={p.displayName} src={p.avatar} />
      <div className="who">
        <div className="named"><b className="ellipsis">{p.displayName}{p.mine && <span className="you-tag">you</span>}</b><NameBadge badge={p.badge} /></div>
        {hidden ? <span className="prog new">{to ? `Finished · ${press} to see` : 'Finished'}</span> : p.done ? <span className="rk">{p.rankName}</span> : <span className="prog">{progressText(p)}</span>}
      </div>
      <span className={`pts${hidden ? ' hidden' : p.done ? '' : ' wait'}`} aria-label={hidden ? 'Score hidden until you open it' : undefined}>{hidden ? '•••' : p.done ? fmt(p.total!) : '—'}</span>
      {r ? <Form form={[r]} /> : <span />}
    </>
  )
  return hidden && to ? <Link className="pr" to={to}>{cells}</Link> : <div className={`pr${p.mine ? ' me' : ''}`}>{cells}</div>
}

/** How a day you've played is going: who's finished, new results, or how it came out. */
function DayChip({ day }: { day: Day }) {
  if (day.fresh.length) return <span className="chip burn sm"><span className="pip" />{day.fresh.length} new</span>
  const joined = day.players.filter((p) => p.status === 'joined' && !day.invitedOnly.has(p.id))
  const done = joined.filter((p) => p.done).length
  const out = outcome(day)
  if (joined.length < 2) return <span className="eyebrow">{day.players.length > joined.length ? 'Invite sent' : 'Nobody yet'}</span>
  if (!out || done < joined.length) return <span className="eyebrow">{done} of {joined.length} finished</span>
  if (out.result === 'w') return <span className="chip gold sm"><Icon name="crown" />You won</span>
  return <span className="chip sm">{out.result === 'l' ? 'You lost' : out.label}</span>
}

/**
 * An edition you've played: everyone you're up against that day, best first, with the new results
 * hidden, and the way to the boards. Outlined in ink until everyone's finished. An invite that came
 * after you played gets its Accept here.
 */
export function DayCard({ day, title, onInvite, onAccept }: { day: Day; title: string; onInvite?(): void; onAccept?(): void }) {
  const joined = day.players.filter((p) => p.status === 'joined')
  const accept = onAccept && day.toAccept.length > 0
  const settled = joined.every((p) => p.done) && day.fresh.length === 0 && day.players.length > 1 && !accept
  const comparable = day.players.some((p) => !p.mine && p.done)
  const code = day.open?.code
  const boards = !accept && code ? `/c/${code}` : undefined
  const from = inviters(day.toAccept)
  // The card's buttons, in order; the row goes when there are none.
  const acts = [
    accept && <button key="accept" type="button" className="btn ink sm" onClick={onAccept}>{from.length === 1 ? `Accept ${from[0].displayName}’s challenge` : `Accept ${day.toAccept.length} challenges`}</button>,
    boards && comparable && (
      <Link key="boards" className={`btn sm ${settled ? 'ghost' : 'ink'}`} to={boards}>
        {day.fresh.length === 1 ? `See ${day.fresh[0].displayName}’s score` : day.fresh.length ? 'See the new scores' : 'Compare boards'}
      </Link>
    ),
    onInvite && <button key="invite" type="button" className="btn ghost sm" onClick={onInvite}><Icon name="link" />{comparable ? 'Invite' : 'Invite more'}</button>,
  ].filter(Boolean)
  return (
    <section className={`card rc${settled ? '' : ' hot'}`} aria-label={`No. ${day.edition}`}>
      <div className="rc-head"><span className="eyebrow">{title}</span><DayChip day={day} /></div>
      {day.players.map((p) => <PlayerRow key={p.id} p={p} day={day} to={boards} />)}
      {acts.length > 0 && <div className="rc-acts">{acts}</div>}
    </section>
  )
}

/** It's your turn on an edition: who's waiting and the score to beat, Play, and Skip for invites. */
export function TurnCard({ day, today, onPlay, onSkip }: { day: Day; today: boolean; onPlay(): void; onSkip?(): void }) {
  const waiting = day.toBeat.length ? day.toBeat : inviters(day.invites)
  const best = day.toBeat[0]?.total ?? null
  const me = day.me
  const label = me && me.rows > 0 ? `Continue No. ${day.edition} · row ${Math.min(me.rows + 1, 6)}` : `Play No. ${day.edition}${best !== null ? ` · beat ${fmt(best)}` : ''}`
  const left = daysLeft(day)
  const count = day.challenges.length
  return (
    <section className="card rc hot" aria-label={`Your turn on No. ${day.edition}`}>
      <div className="rc-head">
        <span className="eyebrow">No. {day.edition} · {today ? 'Today' : weekday(day.date)}</span>
        <span className="eyebrow">{today ? `${count} challenge${count === 1 ? '' : 's'}` : `Closes ${left > 1 ? `in ${left} days` : left === 1 ? 'tomorrow' : 'today'}`}</span>
      </div>
      {waiting.map((p) => (
        <div className="pr np" key={p.id}>
          <Avatar id={p.id} name={p.displayName} src={p.avatar} />
          <div className="who"><div className="named"><b className="ellipsis">{p.displayName}</b><NameBadge badge={p.badge} /></div>{p.done ? <span className="rk">{p.rankName}</span> : <span className="prog">{progressText(p)}</span>}</div>
          <span className={`pts${p.done ? '' : ' wait'}`}>{p.done ? fmt(p.total!) : '—'}</span>
        </div>
      ))}
      <div className="rc-acts"><button type="button" className={`btn block ${today ? 'ink' : 'ghost'}`} onClick={onPlay}>{label}</button></div>
      {(onSkip || waiting.length > 1) && (
        <div className="rc-foot">
          <span>{waiting.length > 1 ? `One play counts against ${waiting.length === 2 ? 'both' : 'all of them'}.` : 'Sent to you in the app'}</span>
          {onSkip && <button type="button" className="btn plain" onClick={onSkip}>Skip</button>}
        </div>
      )}
    </section>
  )
}

/** A past day in one line: when, your score, who was in it, and how it came out. */
export function WeekRow({ day }: { day: Day }) {
  const others = day.players.filter((p) => !p.mine && p.status === 'joined')
  const out = outcome(day)
  const done = others.filter((p) => p.done).length
  const to = day.open ? `/c/${day.open.code}` : day.toAccept[0] ? `/c/${day.toAccept[0].code}` : '/rivals'
  return (
    <Link className="row wk" to={to}>
      <div className="two"><b>No. {day.edition}</b><span>{dayDate(day.date)}{day.me?.done ? ` · you ${fmt(day.me.total!)}` : ''}</span></div>
      <Stack players={others} size={26} />
      {day.toAccept.length ? <span className="chip burn sm res-chip"><span className="pip" />Invited</span>
        : day.fresh.length ? <span className="chip burn sm res-chip"><span className="pip" />{day.fresh.length} new</span>
        : others.length === 0 ? <span className="chip sm res-chip">{day.players.some((p) => !p.mine) ? 'Invite sent' : 'Nobody joined'}</span>
        : out && done === others.length ? <span className={`chip sm res-chip${out.result ? ` ${out.result}` : ''}`}>{out.label}</span>
        : <span className="chip sm res-chip">{done} of {others.length} done</span>}
      <Icon name="next" size={16} />
    </Link>
  )
}

/** A rival: your last five results against them and the record. */
export function RivalRow({ rival: r }: { rival: Rival }) {
  const rec = recordText(r.record, r.player.displayName)
  return (
    <Link className="row rv" to={`/rivals/${r.player.id}`}>
      <Avatar id={r.player.id} name={r.player.displayName} src={r.player.avatar} />
      <div className="two"><div className="named"><b>{r.player.displayName}</b><NameBadge badge={r.player.badge} /></div><Form form={r.record.form} small /></div>
      <div className="rec"><b>{rec.score}</b><span>{rec.lead}</span></div>
      <Icon name="next" size={16} />
    </Link>
  )
}

/**
 * Today's one line about challenges, only when something needs you: today's challengers before you
 * play, then results you haven't seen, then an invite to an edition you've played, then one to an
 * earlier edition.
 */
export function TodayStrip({ days, today }: { days: readonly Day[]; today: number }) {
  const now = days.find((d) => d.edition === today && d.yourTurn)
  const fresh = days.find((d) => d.me?.done && d.fresh.length > 0)
  const accept = days.find((d) => d.toAccept.length > 0)
  const older = days.find((d) => d.edition !== today && d.yourTurn)
  const turnPlayers = (d: Day) => (d.toBeat.length ? d.toBeat : inviters(d.invites))

  let who: readonly ChallengePlayer[]
  let line: ReactNode
  let sub: ReactNode
  if (now) {
    who = turnPlayers(now)
    line = `${namesOf(who)} challenged you`
    sub = who.length <= 2 && who.every((p) => p.done)
      ? who.map((p, i) => <span key={p.id}>{i > 0 && ' · '}{p.displayName} <b>{fmt(p.total!)}</b></span>)
      : now.toBeat[0] ? <><b>{fmt(now.toBeat[0].total!)}</b> to beat</> : 'Play to compare boards'
  } else if (fresh) {
    who = fresh.fresh
    line = `${namesOf(who)} finished No. ${fresh.edition}`
    sub = <span className="new">See where you placed</span>
  } else if (accept) {
    who = inviters(accept.toAccept)
    line = `${namesOf(who)} challenged you to No. ${accept.edition}`
    sub = 'You’ve played it · accept to compare'
  } else if (older) {
    who = turnPlayers(older)
    const left = daysLeft(older)
    line = `${namesOf(who)} challenged you to No. ${older.edition}`
    sub = <>Closes {left > 1 ? `in ${left} days` : left === 1 ? 'tomorrow' : 'today'}{older.toBeat[0] ? <> · <b>{fmt(older.toBeat[0].total!)}</b> to beat</> : null}</>
  } else {
    return null
  }
  return (
    <Link className="strip" to="/rivals">
      <Stack players={who} size={32} max={2} />
      <div><b>{line}</b><span>{sub}</span></div>
      <Icon name="next" size={18} />
    </Link>
  )
}
